// Вход: пароль → код из Telegram (решение Bilol 24.09.2026); ключ доступа (passkey) — запасной вход.
// Первый раз — код настройки, который присылает сервер при запуске, и новый пароль (или ключ доступа).
import { Button } from '@autoaff/ui';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { Eye, EyeOff, KeyRound, LogIn, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, api } from './api';
import { Mark } from './shell/Mark';

export interface LoginMethods {
  password: boolean;
  passkey: boolean;
}

function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'current-password' | 'new-password';
  autoFocus?: boolean;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="pw-field">
      <input
        id={id}
        className="input"
        type={shown ? 'text' : 'password'}
        autoComplete={autoComplete}
        // biome-ignore lint/a11y/noAutofocus: на экране входа поле пароля — единственное действие
        autoFocus={autoFocus}
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="icon-btn"
        aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
        title={shown ? 'Скрыть пароль' : 'Показать пароль'}
        onClick={() => setShown((s) => !s)}
      >
        {shown ? <EyeOff /> : <Eye />}
      </button>
    </div>
  );
}

export function Login({
  registered,
  methods,
  onDone,
}: {
  registered: boolean;
  methods: LoginMethods;
  onDone: () => void;
}) {
  const [step, setStep] = useState<'password' | 'code' | 'passkey-setup'>('password');
  const [setupCode, setSetupCode] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      const err = e as Error;
      setError(
        err.name === 'NotAllowedError'
          ? 'Вход отменён или время ожидания вышло. Попробуйте ещё раз.'
          : err.message,
      );
      // Код устарел — начинаем с пароля
      if (e instanceof ApiError && e.status === 400 && step === 'code') {
        setStep('password');
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  const loginPassword = () =>
    run(async () => {
      const r = await api<{ ok?: boolean; step?: 'code' }>('/api/auth/password/login', { password });
      if (r.step === 'code') {
        setStep('code');
        setResendIn(30);
      } else onDone();
    });

  const sendCode = () =>
    run(async () => {
      await api('/api/auth/password/code', { code });
      onDone();
    });

  const resend = () =>
    run(async () => {
      await api('/api/auth/password/resend', {});
      setResendIn(30);
    });

  const loginPasskey = () =>
    run(async () => {
      const options = await api<Parameters<typeof startAuthentication>[0]['optionsJSON']>(
        '/api/auth/login/options',
        {},
      );
      const response = await startAuthentication({ optionsJSON: options });
      await api('/api/auth/login/verify', { response });
      onDone();
    });

  const setupPassword = () =>
    run(async () => {
      if (password !== repeat) throw new Error('Пароли не совпадают');
      await api('/api/auth/password/setup', { setupCode, password });
      onDone();
    });

  const setupPasskey = () =>
    run(async () => {
      const options = await api<Parameters<typeof startRegistration>[0]['optionsJSON']>(
        '/api/auth/register/options',
        { setupCode },
      );
      const response = await startRegistration({ optionsJSON: options });
      await api('/api/auth/register/verify', { response, name: 'Основное устройство' });
      onDone();
    });

  const head = (
    <div className="brand" style={{ padding: 0 }}>
      <span className="logo">
        <Mark />
      </span>
      <b>AutoAFF</b>
    </div>
  );
  const errorBox = error && (
    <div className="auth-error" role="alert">
      {error}
    </div>
  );

  // ─── первая настройка ───
  if (!registered)
    return (
      <div className="auth">
        <div className="card auth-card">
          {head}
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void (step === 'passkey-setup' ? setupPasskey() : setupPassword());
            }}
          >
            <div>
              <h1>Первая настройка</h1>
              <p style={{ marginTop: 6 }}>
                {step === 'passkey-setup'
                  ? 'Создадим ключ входа для этого устройства: отпечаток, лицо или ПИН.'
                  : 'Придумайте пароль. Когда подключите бота Telegram, при входе к паролю добавится код из Telegram.'}
              </p>
            </div>
            <div>
              <label className="label" htmlFor="setup-code">
                Код настройки
              </label>
              <input
                id="setup-code"
                className="input"
                autoComplete="one-time-code"
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value)}
                placeholder="например, 3fa9c21b"
              />
            </div>
            {step !== 'passkey-setup' && (
              <>
                <div>
                  <label className="label" htmlFor="new-password">
                    Пароль
                  </label>
                  <PasswordInput
                    id="new-password"
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                  />
                  <p className="auth-hint">Не короче 12 символов. Удобнее всего фраза из 3–4 слов.</p>
                </div>
                <div>
                  <label className="label" htmlFor="repeat-password">
                    Пароль ещё раз
                  </label>
                  <PasswordInput
                    id="repeat-password"
                    value={repeat}
                    onChange={setRepeat}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              icon={step === 'passkey-setup' ? <KeyRound /> : <LogIn />}
              disabled={
                busy || setupCode.trim().length < 4 || (step !== 'passkey-setup' && password.length < 12)
              }
            >
              {step === 'passkey-setup'
                ? busy
                  ? 'Создаю ключ…'
                  : 'Создать ключ входа'
                : busy
                  ? 'Сохраняю…'
                  : 'Задать пароль и войти'}
            </Button>
            <button
              type="button"
              className="auth-link"
              onClick={() => {
                setError(null);
                setStep(step === 'passkey-setup' ? 'password' : 'passkey-setup');
              }}
            >
              {step === 'passkey-setup' ? 'Лучше задать пароль' : 'Вместо пароля — ключ доступа'}
            </button>
          </form>
          {errorBox}
        </div>
      </div>
    );

  // ─── второй шаг: код из Telegram ───
  if (step === 'code')
    return (
      <div className="auth">
        <div className="card auth-card">
          {head}
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void sendCode();
            }}
          >
            <div>
              <h1>Код из Telegram</h1>
              <p style={{ marginTop: 6 }}>Бот AutoAFF прислал вам 6 цифр. Код действует 5 минут.</p>
            </div>
            <div>
              <label className="label" htmlFor="login-code">
                Код
              </label>
              <input
                id="login-code"
                className="input code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                // biome-ignore lint/a11y/noAutofocus: после пароля единственное действие — ввести код
                autoFocus
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ''))}
                placeholder="123 456"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              icon={<LogIn />}
              disabled={busy || code.replace(/\D/g, '').length !== 6}
            >
              {busy ? 'Проверяю…' : 'Войти'}
            </Button>
            <div className="flex justify-between gap-2 flex-wrap">
              <button
                type="button"
                className="auth-link"
                onClick={() => {
                  setStep('password');
                  setCode('');
                  setError(null);
                }}
              >
                Назад
              </button>
              <button
                type="button"
                className="auth-link"
                disabled={resendIn > 0 || busy}
                onClick={() => void resend()}
              >
                <Send aria-hidden="true" />
                {resendIn > 0 ? `Прислать код ещё раз через ${resendIn} с` : 'Прислать код ещё раз'}
              </button>
            </div>
          </form>
          {errorBox}
        </div>
      </div>
    );

  // ─── вход ───
  return (
    <div className="auth">
      <div className="card auth-card">
        {head}
        <div>
          <h1>Вход</h1>
          <p style={{ marginTop: 6 }}>
            {methods.password
              ? 'Введите пароль — затем код, который пришлёт бот в Telegram.'
              : 'Используйте ключ этого устройства: отпечаток, лицо или ПИН.'}
          </p>
        </div>
        {methods.password && (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (password) void loginPassword();
            }}
          >
            <div>
              <label className="label" htmlFor="password">
                Пароль
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              icon={<LogIn />}
              disabled={busy || !password}
            >
              {busy ? 'Проверяю…' : 'Продолжить'}
            </Button>
          </form>
        )}
        {methods.passkey && (
          <Button
            variant={methods.password ? 'ghost' : 'primary'}
            size={methods.password ? undefined : 'lg'}
            block
            icon={<KeyRound />}
            disabled={busy}
            onClick={() => void loginPasskey()}
          >
            {methods.password ? 'Войти ключом доступа' : busy ? 'Проверяю…' : 'Войти с ключом'}
          </Button>
        )}
        {errorBox}
      </div>
    </div>
  );
}
